import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { PERMISSIONS } from "@/constants/permissions"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import useUser from "@/hooks/useUser"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import {
  pilotRequests,
  type PilotRequest,
  type ResearchOutcomeStatus,
} from "./pilotRequestsApi"
export function RequestOutcomePanel({ request }: { request: PilotRequest }) {
  const { user, hasPermission } = useUser()
  const { translate: t } = useTranslation()
  const client = useQueryClient()
  const [pair, setPair] = useState("")
  const [status, setStatus] = useState<ResearchOutcomeStatus>("needs_research")
  const [url, setUrl] = useState("")
  const [reason, setReason] = useState("")
  const pairs = request.scope.requirements.flatMap((r) =>
    r.frameworks.map((framework) => ({ partNumber: r.partNumber, framework })),
  )
  const chosen = pair === "" ? undefined : pairs[Number(pair)]
  const save = useMutation({
    mutationFn: () =>
      pilotRequests.saveOutcome(request.id, {
        expectedRevision: request.revision,
        ...chosen!,
        status,
        reason: reason.trim(),
        checkedUrl: url.trim(),
      }),
    onSuccess: () => {
      void client.invalidateQueries({
        queryKey: [
          "company-index",
          user?.organization,
          "research-requests",
          request.companyId,
        ],
      })
      setReason("")
    },
  })
  const labels = {
    no_public_declaration: t("CI_OUTCOME_MISSING"),
    blocked: t("CI_OUTCOME_BLOCKED"),
    needs_research: t("CI_OUTCOME_PENDING"),
  }
  return (
    <details className="mt-4 rounded border p-3">
      <summary>{t("CI_OUTCOME_TITLE")}</summary>
      <p className="my-3 text-sm text-muted-foreground">
        {t("CI_OUTCOME_HINT")}
      </p>
      {request.outcomes?.map((o, i) => (
        <p className="text-sm" key={i}>
          {o.partNumber} · {o.framework.toUpperCase()} · {labels[o.status]} ·{" "}
          {o.reason} · {o.actor} · {new Date(o.checkedAt).toLocaleString()}
        </p>
      ))}
      {hasPermission(PERMISSIONS.MC_DECLARATIONS_UPLOAD) && (
        <form
          className="space-y-3 mt-3"
          onSubmit={(e) => {
            e.preventDefault()
            if (chosen && url.trim() && reason.trim() && !save.isPending)
              save.mutate()
          }}
        >
          <label className="block">
            {t("CI_OUTCOME_PAIR")}
            <select
              className="block w-full rounded border bg-background p-2"
              value={pair}
              onChange={(e) => setPair(e.target.value)}
            >
              <option value="">—</option>
              {pairs.map((p, i) => (
                <option value={i} key={i}>
                  {p.partNumber} · {p.framework.toUpperCase()}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            {t("CI_OUTCOME_STATUS")}
            <select
              className="block w-full rounded border bg-background p-2"
              value={status}
              onChange={(e) =>
                setStatus(e.target.value as ResearchOutcomeStatus)
              }
            >
              {Object.entries(labels).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            {t("CI_OUTCOME_URL")}
            <Input
              type="url"
              required
              value={url}
              onChange={(e) => setUrl(e.target.value)}
            />
          </label>
          <label className="block">
            {t("CI_OUTCOME_REASON")}
            <Textarea
              required
              maxLength={2000}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </label>
          <Button
            disabled={
              !chosen || !url.trim() || !reason.trim() || save.isPending
            }
          >
            {t("CI_OUTCOME_SAVE")}
          </Button>
          {save.isError && <p role="alert">{t("CI_MATCH_ERROR")}</p>}
        </form>
      )}
    </details>
  )
}
